import { useCallback, useEffect, useRef } from 'react';
import cross from '~/assets/icons/cross.svg';
import { playSound, Sound } from '~/assets/sounds';
import {
	Button,
	ButtonBorderRadius,
	ButtonColors,
	ButtonHeight,
} from '~/components/ui/Button';
import { Divider } from '~/components/ui/Divider';
import { Text } from '~/components/ui/Text';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction } from '~/shared';
import {
	setModalChangeNamePendingVisible,
	setModalChangeNameRejectedVisible,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	StyledContainer,
	StyledGrid,
	StyledHeadContainer,
	StyledNicknameSummary,
} from '../ModalChangeName/ModalChangeName.styled';

const ModalChangeNamePendingPanel = () => {
	const dispatch = useLayoutDispatch();
	const pendingVisible = useLayoutSelector(
		(state) => state.modal.modalChangeNamePendingVisible,
	);
	const application = useObservable(rage.donate.getChangeNameApplication());

	const lastPendingSnapshotRef = useRef<{
		firstName: string;
		lastName: string;
	} | null>(null);

	useEffect(() => {
		if (application?.status === 'pending') {
			lastPendingSnapshotRef.current = {
				firstName: application.firstName,
				lastName: application.lastName,
			};
		}
	}, [application]);

	const close = useCallback(() => {
		dispatch(setModalChangeNamePendingVisible(false));
	}, [dispatch]);

	const handleCancelRequest = useCallback(() => {
		playSound(Sound.ClickButton);
		dispatch(setModalChangeNamePendingVisible(false));
		rage.donate
			.cancelChangeName()
			.then(() => {
				rage.notifications.showSuccess('Заявка на смену имени отменена');
			})
			.catch((err) => {
				rage.notifications.showException(err);
				dispatch(setModalChangeNamePendingVisible(true));
			});
	}, [dispatch]);

	useInputAction(InputAction.CommonMenuClose, close);

	useEffect(() => {
		if (pendingVisible && application && application.status === 'rejected') {
			dispatch(setModalChangeNamePendingVisible(false));
			dispatch(setModalChangeNameRejectedVisible(true));
		}
	}, [pendingVisible, application, dispatch]);

	if (application?.status === 'rejected') {
		return null;
	}

	const pendingDisplay =
		application?.status === 'pending'
			? application
			: lastPendingSnapshotRef.current;

	if (!pendingDisplay) {
		return null;
	}

	return (
		<StyledContainer>
			<StyledHeadContainer>
				<Text>Смена никнейма</Text>
				<Button
					color={ButtonColors.Default}
					height={ButtonHeight.Height20}
					borderRadius={ButtonBorderRadius.Radius5}
					style={{ width: 20 }}
					onClick={() => {
						close();
						playSound(Sound.ClickButton);
					}}
				>
					<img src={cross} width={10} height={10} />
				</Button>
			</StyledHeadContainer>
			<Divider
				$direction={'H'}
				style={{ marginTop: 30, marginBottom: 30, opacity: 0.1 }}
			/>
			<Text style={{ fontSize: 16, color: '#ffffff', opacity: 0.75 }}>
				Ваша заявка по смене ника находится на рассмотрении администрации.
				<br />
				<br />
				Примерный срок ожидания до 3-х дней.
			</Text>
			<StyledNicknameSummary>
				<Text>Новый никнейм</Text>
				<Text>
					{`${pendingDisplay.firstName} ${pendingDisplay.lastName}`.trim()}
				</Text>
			</StyledNicknameSummary>
			<Divider
				$direction={'H'}
				style={{ marginTop: 20, marginBottom: 30, opacity: 0.1 }}
			/>
			<StyledGrid style={{ gap: 20, marginTop: 0 }}>
				<Button
					color={ButtonColors.Default}
					height={ButtonHeight.Height50}
					onClick={() => {
						close();
						playSound(Sound.ClickButton);
					}}
				>
					<Text>Закрыть окно</Text>
				</Button>
				<Button
					color={ButtonColors.Cancel}
					height={ButtonHeight.Height50}
					onClick={handleCancelRequest}
				>
					<Text>Отменить смену</Text>
				</Button>
			</StyledGrid>
		</StyledContainer>
	);
};

export { ModalChangeNamePendingPanel };
